import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { LockIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { resetPassword } from '../../api/auth/reset-password'
import TranslatedForm, {
  FormControl,
  FormField,
  FormItem
} from '../../components/elements/forms/form'
import ControlledInput from '../../components/elements/inputs/controlled-input'
import AuthLayout from '../../components/layouts/auth-layout'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import useTranslate from '../../lib/hooks/use-translate'
import { ResetPasswordInput } from '../../lib/types/models/auth/reset-password'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { PASSWORD_VALIDATION } from '../../lib/utils/validations/password.validation'
import { createStringSchema } from '../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../utils/constants/form.constants'

export default function ResetPasswordPage() {
  const { t, currentLanguage } = useTranslate()
  const { navigate } = useNavigator()
  const { logout } = useAuth()

  const formSchema: ZodType<ResetPasswordInput> = z.object({
    code: createStringSchema('code', {
      minimumLength: 6,
      maximumLength: 6,
      language: currentLanguage
    }),
    newPassword: PASSWORD_VALIDATION
  })

  const { mutate, isPending } = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      logout()
      setTimeout(() => {
        navigate(Paths.AUTH_LOGIN_PAGE)
      }, 500)
    },
    onError: showAxiosError
  })

  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
      newPassword: ''
    },
    mode: FORM_MODE
  })

  function onSubmit(values: ResetPasswordInput) {
    mutate(values)
  }

  return (
    <AuthLayout text={t('reset.password.title')} useTranslation>
      <Typography variant="h1" className="mb-6">
        {t('reset.password.subtitle')}
      </Typography>
      <TranslatedForm<ResetPasswordInput> form={form}>
        <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    type="text"
                    inputMode="numeric"
                    placeholder="Code"
                    label="Code"
                    autoComplete="off"
                    {...field}
                    wrapperClassName="bg-white"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="h-4" />

          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    type="password"
                    placeholder={t('reset.password.new')}
                    label={t('reset.password.new')}
                    wrapperClassName="bg-white"
                    icon={LockIcon}
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            loading={isPending}
            type="submit"
            label={t('button.confirm')}
            className="w-full mt-10 mobile:bg-blue-dark-active"
          />
        </form>
      </TranslatedForm>
    </AuthLayout>
  )
}
